// ============================================================================
// RecentActivityFeed — the merged recent-activity timeline (Wave 4).
// ----------------------------------------------------------------------------
// Renders home.recent_activity: the last ~10 merged meals + own/suggested
// exercises + water events, newest first. Each row wears its provenance/kind
// COLOUR + icon (amber meal, lime own exercise, violet coach homework, ocean
// water) so it speaks the same colour language as the calendar legend
// (ENTRY_STYLE), with a relative time and a one-line summary. The list staggers
// in with Framer Motion, fully guarded by prefers-reduced-motion.
//
// All user-facing copy comes from i18n keys (clientPortal.feed.* + the activity
// / nutrition namespaces) — never a server display string (Wave 2 leak guard).
// The only server text rendered is the client's own meal/exercise title (their
// own free text, safe per the backend contract).
// ============================================================================
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Card, CardHeader } from "../../components";
import type { ClientHomeActivity } from "../../api";
import { cn } from "../../lib/cn";
import { fmtRelative } from "./portalLib";

type FeedColour = "amber" | "lime" | "violet" | "ocean";

const FEED_STYLE: Record<FeedColour, { dot: string; soft: string; text: string }> = {
  amber: { dot: "bg-grad-amber", soft: "bg-amber-soft", text: "text-amber-600" },
  lime: { dot: "bg-grad-lime", soft: "bg-lime-soft", text: "text-lime-600" },
  violet: { dot: "bg-grad-violet", soft: "bg-violet-soft", text: "text-ember-600" },
  ocean: { dot: "bg-grad-ocean", soft: "bg-ocean-soft", text: "text-ocean-600" },
};

function MealIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path d="M5 3v8m0 0a2 2 0 0 0 2-2V3M3 3v6a2 2 0 0 0 2 2m0 0v10M16 3c-1.7 0-3 2-3 5s1 4 3 4m0 0v9m0-9c1.7 0 3-1 3-4s-1.3-5-3-5"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function DumbbellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path d="M6.5 6.5l11 11M4 9l-1.5 1.5a2 2 0 0 0 0 2.8L4 15M9 4l-1.5 1.5M20 9l1.5 1.5a2 2 0 0 1 0 2.8L20 15M15 20l1.5-1.5"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function WaterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path d="M12 3.5c3.6 4.2 6 7.4 6 10.5a6 6 0 0 1-12 0c0-3.1 2.4-6.3 6-10.5z"
        stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

/** Resolve a feed row into colour + icon + localized title/summary. */
function useFeedRow() {
  const { t } = useTranslation("clientPortal");
  const { t: ta } = useTranslation("activity");
  const { t: tn } = useTranslation("nutrition");

  const typeLabel = (raw: string | null): string => {
    const key = (raw ?? "").toLowerCase();
    const known = ["strength", "cardio", "mobility", "hiit", "mixed"];
    return known.includes(key) ? ta(`type.${key}`) : raw ?? "";
  };
  const mealLabel = (raw: string | null): string => {
    const key = (raw ?? "").toLowerCase();
    const known = ["breakfast", "lunch", "dinner", "snack"];
    return known.includes(key) ? tn(`meal.${key}`) : "";
  };

  return (item: ClientHomeActivity): {
    colour: FeedColour;
    icon: ReactNode;
    title: string;
    summary: string;
  } => {
    if (item.kind === "meal") {
      const named = (item.title ?? "").trim();
      const mealKind = mealLabel(item.meal);
      const title = named || mealKind || tn("trainer.untitledMeal");
      const summary =
        item.kcal != null
          ? tn("marker.dayTotal", { kcal: Math.round(item.kcal) })
          : tn("meals");
      return { colour: "amber", icon: <MealIcon />, title, summary };
    }
    if (item.kind === "water") {
      return {
        colour: "ocean",
        icon: <WaterIcon />,
        title: tn("water.title"),
        summary: `${Math.round(item.ml)} ${tn("water.mlUnit")}`,
      };
    }
    // activity (own | suggested)
    const isSuggested = item.source === "suggested";
    const named = (item.title ?? "").trim();
    const title = named || typeLabel(item.activity_type) || ta("type.fallback");
    const summary = isSuggested
      ? t("feed.kind.homework")
      : t("feed.kind.ownExercise");
    return {
      colour: isSuggested ? "violet" : "lime",
      icon: <DumbbellIcon />,
      title,
      summary,
    };
  };
}

function FeedRow({ item, index }: { item: ClientHomeActivity; index: number }) {
  const reduce = useReducedMotion();
  const resolve = useFeedRow();
  const { colour, icon, title, summary } = resolve(item);
  const st = FEED_STYLE[colour];

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: reduce ? 0 : Math.min(index * 0.05, 0.4) }}
      className="flex items-center gap-3"
    >
      {/* timeline rail dot */}
      <span
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-full",
          st.soft,
          st.text,
        )}
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-body">{title}</p>
        <p className="truncate text-xs text-body-soft">{summary}</p>
      </div>
      <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-body-faint">
        {fmtRelative(item.ts)}
      </span>
    </motion.li>
  );
}

export function RecentActivityFeed({ items }: { items: ClientHomeActivity[] }) {
  const { t } = useTranslation("clientPortal");

  return (
    <Card>
      <CardHeader title={t("feed.title")} subtitle={t("feed.subtitle")} />
      {items.length === 0 ? (
        <div className="rounded-lg bg-surface-sunken px-4 py-8 text-center">
          <p className="font-display font-bold text-body">{t("feed.empty")}</p>
          <p className="mt-1 text-sm text-body-soft">{t("feed.emptyHint")}</p>
        </div>
      ) : (
        <ul className="space-y-3.5">
          {items.map((item, i) => (
            <FeedRow key={`${item.kind}-${item.id}-${i}`} item={item} index={i} />
          ))}
        </ul>
      )}
    </Card>
  );
}
